import {
  AreaDefinition,
  BoundaryRequest,
  InventoryBoundary,
  InventoryCrops,
  getFeatureAcres,
} from '@cibo/core'
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  AlertTitle,
  Button,
  CardActionArea,
  CardContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Stack,
  Typography,
  styled,
} from '@mui/material'
import { Field, Form, Formik } from 'formik'
import { Select, TextField } from 'formik-mui'
import { Feature, Geometry } from 'geojson'
import { equals } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { array, number, object, string } from 'yup'
import { BoundaryThumbnail } from '../../../../../../features/Inventories/components/BoundaryThumbnail'
import { useBoundary } from '../../../../../../features/Inventories/queries/useBoundary'
import { useCreateBoundary } from '../../../../../../features/Inventories/queries/useCreateBoundary'
import { useUpdateBoundary } from '../../../../../../features/Inventories/queries/useUpdateBoundary'
import { useGeometryAcres } from '../../../../../../hooks/useFindAcres'
import { ROUTE } from '../../../../../../navigation/types'
import { fipsToName } from '../../../../../../utils'
import { EditBoundaryOverlaySkeleton } from './EditBoundaryOverlaySkeleton'

const EMPTY_CROPS = ({ crop: '', acresSourced: '' } as unknown) as InventoryCrops
const CROP_OPTIONS = ['corn', 'soybeans']
const AREA_TYPE_OPTIONS = ['supplyShed', 'directSource']

export interface EditBoundaryProps {
  features?: Feature<Geometry, { resourceId: string }>[]
  selectedFips?: string[]
  clearSelectedFips: () => void
  clearSelectedFeatures: () => void
}

// This is a workaround due to mui-formik and mui broadly not supporting shrink in compatible ways
const NotchedCropsSelect = styled(Select)(() => ({
  '.MuiOutlinedInput-notchedOutline legend': { maxWidth: '100%', visibility: 'hidden' },
  '.MuiOutlinedInput-notchedOutline legend:before': {
    content: '"crop*"',
  },
}))

// This is a workaround due to mui-formik and mui broadly not supporting shrink in compatible ways
const NotchedTypeSelect = styled(Select)(() => ({
  '.MuiOutlinedInput-notchedOutline legend': { maxWidth: '100%', visibility: 'hidden' },
  '.MuiOutlinedInput-notchedOutline legend:before': {
    content: '"type*"',
  },
}))

const NotchedTextField = styled(TextField)(() => ({
  '.MuiOutlinedInput-notchedOutline legend': { maxWidth: '100%' },
}))

export const EditBoundaryOverlay = (props: EditBoundaryProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation('impact/EditBoundaryOverlay')
  const [params] = useSearchParams()
  const { inventoryId } = useParams()
  const boundaryId = params.get('id')
  const { data: boundary, isLoading } = useBoundary(inventoryId, boundaryId)
  const updateBoundary = useUpdateBoundary()
  const createBoundary = useCreateBoundary()
  const fipsAcres = useGeometryAcres(props.selectedFips || [])

  const acres = boundaryId
    ? boundary?.item?.acreage
    : props.selectedFips
    ? fipsAcres.data
    : getFeatureAcres(props.features || [])

  if (!inventoryId) {
    navigate(ROUTE.INVENTORIES)
  }

  if (isLoading || fipsAcres.isLoading) {
    return <EditBoundaryOverlaySkeleton />
  }

  const displayName =
    props.selectedFips && props.selectedFips.length > 0
      ? fipsToName(
          props.selectedFips.map(a => a.slice(5)),
          t
        )
      : ''

  const areaDef: AreaDefinition[] = []
  if (!!props.features) {
    props.features.forEach(feature => {
      areaDef.push({
        type: 'feature',
        // @ts-ignore does this need to be recursive?
        feature,
      })
    })
  }
  if (!!props.selectedFips) {
    props.selectedFips.forEach(fipsCode => {
      areaDef.push({
        type: 'fips',
        fips: fipsCode,
      })
    })
  }

  return (
    <Formik
      validateOnMount
      validateOnChange
      initialValues={{
        ...boundary?.item,
        name: boundary?.item.name ? boundary.item.name : displayName,
        crops: !!boundary?.item?.crops?.length ? boundary.item.crops : [EMPTY_CROPS],
      }}
      validationSchema={object().shape({
        name: string().required(t('required')),
        type: string()
          .oneOf(AREA_TYPE_OPTIONS)
          .required(t('typeRequired')),
        crops: array()
          .of(
            object().shape({
              crop: string()
                .oneOf(CROP_OPTIONS)
                .required(t('cropTypeRequired')),
              acresSourced: number().required(t('acresRequired')),
            })
          )
          .required(t('cropsRequired')),
      })}
      onSubmit={({ crops, boundaryId, name, type }, { setSubmitting }) => {
        const finalCrops = crops?.filter((a: InventoryCrops) => !equals(a, EMPTY_CROPS))
        const valuesToSubmit: BoundaryRequest | InventoryBoundary = {
          name,
          type,
          crops: finalCrops as InventoryCrops[],
        }
        if (boundaryId) {
          updateBoundary
            .mutateAsync({
              // UI is redirected if no inventoryId is present
              // eslint-disable-next-line
              inventoryId: inventoryId!,
              boundaryId,
              data: valuesToSubmit,
            })
            .then(() => {
              props.clearSelectedFips()
              props.clearSelectedFeatures()
              navigate('./')
            })
            .finally(() => setSubmitting(false))
        } else {
          const areaDef: AreaDefinition[] = []
          if (!!props.features) {
            props.features.forEach(feature => {
              areaDef.push({
                type: 'feature',
                // @ts-ignore does this need to be recursive?
                feature,
              })
            })
          }
          if (!!props.selectedFips) {
            props.selectedFips.forEach(fipsCode => {
              const strippedFips = fipsCode.split(':')[1]
              strippedFips &&
                areaDef.push({
                  type: 'fips',
                  fips: strippedFips,
                })
            })
          }
          valuesToSubmit.areaDef = areaDef
          createBoundary
            .mutateAsync({
              // UI is redirected if no inventoryId is present
              // eslint-disable-next-line
              inventoryId: inventoryId!,
              data: valuesToSubmit,
            })
            .then(() => {
              props.clearSelectedFips()
              props.clearSelectedFeatures()
              navigate('./')
            })
            .finally(() => setSubmitting(false))
        }
      }}
    >
      {({ values, setValues, touched, isValid }) => {
        const crops = values.crops
        const addCrop = () =>
          setValues({ ...values, crops: [...values.crops, EMPTY_CROPS] as InventoryCrops[] }, true)
        const removeCrop = (index: number) => {
          const cropsValue = values.crops
          cropsValue.splice(index, 1)
          setValues({ ...values, crops: cropsValue }, true)
        }
        return (
          <Form>
            <CardContent sx={{ padding: 3 }}>
              <Stack direction="row" spacing={1.5} pb={3}>
                <BoundaryThumbnail
                  boundary={{ areaDef, name: displayName || t('newBoundary') }}
                  height={36}
                  width={36}
                  style={{ borderRadius: 0.5 }}
                />
                <Stack direction="column">
                  <Typography variant="body1">
                    {boundary?.item?.name || t('editDetails')}
                  </Typography>
                  {!!acres && (
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('acreage', { count: acres })}
                    </Typography>
                  )}
                </Stack>
              </Stack>
              <Stack spacing={2} alignItems="start" style={{ pointerEvents: 'all', width: '100%' }}>
                <FormControl sx={{ width: '100%' }}>
                  <InputLabel shrink htmlFor={`boundary-name`}>
                    {t('areaName')}
                  </InputLabel>
                  <Field
                    name="name"
                    id="name"
                    component={NotchedTextField}
                    InputProps={{
                      size: 'medium',
                      id: 'boundary-name',
                      inputLabelProps: { shrink: true },
                      label: t('areaName'),
                      style: { pointerEvents: 'all', width: '100%' },
                    }}
                  />
                </FormControl>

                <Stack component="ul" spacing={1.5} style={{ width: '100%' }}>
                  {crops.map((crop, index) => {
                    return (
                      <Stack key={`${crop}.${index}`} component="li" direction="row" spacing={2}>
                        <FormControl sx={{ flex: 1 }}>
                          <InputLabel shrink htmlFor={`crop-input-${index}`} required={index === 0}>
                            {t('crop')}
                          </InputLabel>
                          <Field
                            size="medium"
                            name={`crops.${index}.crop`}
                            id={`crops.${index}.crop`}
                            component={NotchedCropsSelect}
                          >
                            {CROP_OPTIONS.map(option => (
                              <MenuItem key={option} value={option}>
                                {t(`option_${option}`)}
                              </MenuItem>
                            ))}
                          </Field>
                        </FormControl>{' '}
                        <FormControl sx={{ flex: 1 }}>
                          <InputLabel shrink htmlFor={`crop-acres-${index}`} required={index === 0}>
                            {t('avgAnnualAcresSourced')}
                          </InputLabel>
                          <Field
                            name={`crops.${index}.acresSourced`}
                            id={`crops.${index}.acresSourced`}
                            InputProps={{
                              size: 'medium',
                              inputLabelProps: { shrink: true },
                              label: t('avgAnnualAcresSourced'),
                              style: { pointerEvents: 'all' },
                              id: `crop-acres-${index}`,
                              type: 'number',
                            }}
                            component={NotchedTextField}
                          />
                        </FormControl>
                        {crops.length > 1 && (
                          <IconButton
                            aria-label={t('remove')}
                            style={{ pointerEvents: 'all' }}
                            onClick={() => removeCrop(index)}
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        )}
                      </Stack>
                    )
                  })}
                </Stack>
                <Button
                  color="secondary"
                  onClick={addCrop}
                  disabled={crops.length >= CROP_OPTIONS.length}
                  style={{ pointerEvents: 'all' }}
                >
                  {t('addCrop')}
                </Button>
                <FormControl sx={{ width: '55%' }}>
                  <InputLabel shrink id="area-type" required>
                    {t('type')}
                  </InputLabel>
                  <Field
                    size="medium"
                    name="type"
                    id="type"
                    placeholder={t('selectType')}
                    component={NotchedTypeSelect}
                    InputProps={{
                      labelId: 'area-type',
                      id: `area-type`,
                      InputLabelProps: { shrink: true },
                      label: t('type'),
                      displayEmpty: true,
                      style: { pointerEvents: 'all', width: '100%' },
                      notched: true,
                    }}
                  >
                    {AREA_TYPE_OPTIONS.map(option => (
                      <MenuItem key={option} value={option}>
                        {t(`option_${option}`)}
                      </MenuItem>
                    ))}
                  </Field>
                </FormControl>
                {(createBoundary.isError || updateBoundary.isError) && (
                  <Alert severity="error">
                    <AlertTitle>{t('errorSavingBoundary')}</AlertTitle>
                    {createBoundary.error?.response?.data.message ||
                      updateBoundary.error?.response?.data.message}
                  </Alert>
                )}
              </Stack>
            </CardContent>
            <CardActionArea
              sx={{ padding: 2, display: 'flex', justifyContent: 'space-between' }}
              style={{ pointerEvents: 'none' }}
              component="div"
            >
              <Button
                onClick={() => navigate(-1)}
                variant="text"
                color="secondary"
                style={{ pointerEvents: 'all' }}
              >
                {t('cancel')}
              </Button>
              <LoadingButton
                loading={updateBoundary.isLoading || createBoundary.isLoading}
                type="submit"
                variant="outlined"
                color="secondary"
                style={{ pointerEvents: 'all' }}
                disabled={!touched || !isValid}
              >
                {t('save')}
              </LoadingButton>
            </CardActionArea>
          </Form>
        )
      }}
    </Formik>
  )
}
